import { useEffect } from "react";

const MODAL_SELECTORS = [
  '[aria-modal="true"]',
  "[data-modal-open=\"true\"]",
  ".crt-modal",
  ".crt-popup",
  ".crt-modal-overlay",
  ".crt-popup-overlay",
  ".ReactModal__Overlay",
];

const isVisible = (element) => {
  if (!element) return false;
  if (element.hasAttribute("hidden")) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (style.pointerEvents === "none") return false;
  const opacity = Number(style.opacity);
  if (Number.isFinite(opacity) && opacity < 0.05) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
};

const hasOpenModal = () => {
  return MODAL_SELECTORS.some((selector) => {
    const nodes = document.querySelectorAll(selector);
    return Array.from(nodes).some((node) => isVisible(node));
  });
};

export default function GlobalModalScrollLock() {
  useEffect(() => {
    let rafId = null;
    let isLocked = false;
    let lockedScrollY = 0;
    let lockedPaddingRight = "";
    let lockedHtmlOverflow = "";
    let lockedHtmlHeight = "";
    let lockedBodyOverflow = "";
    let lockedHtmlScrollBehavior = "";
    let lockedBodyScrollBehavior = "";

    const lockScroll = () => {
      if (isLocked) return;
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      const scrollbarGap =
        window.innerWidth - document.documentElement.clientWidth;

      lockedHtmlOverflow = document.documentElement.style.overflow || "";
      lockedHtmlHeight = document.documentElement.style.height || "";
      lockedBodyOverflow = document.body.style.overflow || "";

      document.documentElement.classList.add("no-scroll");
      document.body.classList.add("no-scroll");
      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.height = "100%";
      document.body.style.position = "fixed";
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      if (scrollbarGap > 0) {
        lockedPaddingRight = document.body.style.paddingRight || "";
        document.body.style.paddingRight = `${scrollbarGap}px`;
      }
      isLocked = true;
    };

    const unlockScroll = () => {
      if (!isLocked) return;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = lockedBodyOverflow;
      document.body.style.paddingRight = lockedPaddingRight;
      document.documentElement.classList.remove("no-scroll");
      document.body.classList.remove("no-scroll");
      document.documentElement.style.overflow = lockedHtmlOverflow;
      document.documentElement.style.height = lockedHtmlHeight;
      lockedHtmlScrollBehavior = document.documentElement.style.scrollBehavior || "";
      lockedBodyScrollBehavior = document.body.style.scrollBehavior || "";
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";
      window.scrollTo(0, lockedScrollY);
      window.requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = lockedHtmlScrollBehavior;
        document.body.style.scrollBehavior = lockedBodyScrollBehavior;
      });
      isLocked = false;
    };

    const updateBodyScrollLock = () => {
      if (hasOpenModal()) {
        lockScroll();
      } else {
        unlockScroll();
      }
    };

    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateBodyScrollLock();
      });
    };

    const observer = new MutationObserver(() => {
      scheduleUpdate();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden", "aria-modal", "hidden", "data-modal-open"],
    });

    scheduleUpdate();

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      unlockScroll();
    };
  }, []);

  return null;
}
